import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContactNumbers, setContactNumbers, clearMessages } from '../redux/slices/contactNumberSlice';

const AdminContactManagement = () => {
    const dispatch = useDispatch();
    const { contactNumbers, loading, error, success } = useSelector((state) => state.contactNumbers);
    const [formData, setFormData] = useState({
        packageType: 'Domestic',
        phone: '',
        whatsapp: '',
        phone2: '',
        isActive: true,
    });

    const packageTypes = ['Domestic', 'International', 'Short', 'Customised', 'Activities'];

    // Fetch contact numbers on mount
    useEffect(() => {
        dispatch(fetchContactNumbers());
        return () => {
            dispatch(clearMessages());
        };
    }, [dispatch]);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        dispatch(clearMessages());

        // Validate phone numbers
        const phoneRegex = /^\+\d{10,15}$/;
        if (!phoneRegex.test(formData.phone)) {
            dispatch({
                type: 'contactNumbers/setContactNumbers/rejected',
                payload: 'Please provide a valid phone number with country code (e.g., +911234567890)',
            });
            return;
        }
        if (!phoneRegex.test(formData.whatsapp)) {
            dispatch({
                type: 'contactNumbers/setContactNumbers/rejected',
                payload: 'Please provide a valid WhatsApp number with country code (e.g., +911234567890)',
            });
            return;
        }
        if (formData.phone2 && !phoneRegex.test(formData.phone2)) {
            dispatch({
                type: 'contactNumbers/setContactNumbers/rejected',
                payload: 'Please provide a valid secondary phone number with country code or leave it empty',
            });
            return;
        }

        dispatch(setContactNumbers(formData)).then((result) => {
            if (result.meta.requestStatus === 'fulfilled') {
                setFormData({
                    packageType: 'Domestic',
                    phone: '',
                    whatsapp: '',
                    phone2: '',
                    isActive: true,
                });
            }
        });
    };

    return (
        <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold text-custom-blue mb-6">Manage Contact Numbers</h1>

            {/* Error and Success Messages */}
            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
                    {error}
                </div>
            )}
            {success && (
                <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">
                    {success}
                </div>
            )}

            {/* Form to Set Contact Numbers */}
            <div className="bg-custom-white shadow-sm p-6 rounded-lg mb-8">
                <h2 className="text-2xl font-semibold text-custom-blue mb-4">Set Contact Numbers</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-custom-blue">Package Type</label>
                        <select
                            name="packageType"
                            value={formData.packageType}
                            onChange={handleInputChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-custom-blue focus:ring-custom-yellow focus:border-custom-yellow"
                        >
                            {packageTypes.map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-custom-blue">Phone Number</label>
                        <input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="+911234567890"
                            className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-custom-blue focus:ring-custom-yellow focus:border-custom-yellow"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-custom-blue">WhatsApp Number</label>
                        <input
                            type="text"
                            name="whatsapp"
                            value={formData.whatsapp}
                            onChange={handleInputChange}
                            placeholder="+911234567891"
                            className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-custom-blue focus:ring-custom-yellow focus:border-custom-yellow"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-custom-blue">Secondary Phone Number (Optional)</label>
                        <input
                            type="text"
                            name="phone2"
                            value={formData.phone2}
                            onChange={handleInputChange}
                            placeholder="+911234567892"
                            className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-custom-blue focus:ring-custom-yellow focus:border-custom-yellow"
                        />
                    </div>
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleInputChange}
                            className="h-4 w-4 text-custom-yellow focus:ring-custom-yellow border-gray-300 rounded"
                        />
                        <label className="ml-2 text-sm font-medium text-custom-blue">Active</label>
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full bg-custom-yellow text-custom-blue py-2 rounded-lg font-medium hover:bg-custom-blue hover:text-white transition-colors duration-200 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {loading ? 'Submitting...' : 'Set Contact Numbers'}
                    </button>
                </form>
            </div>

            {/* Table to Display Contact Numbers */}
            <div className="bg-custom-white shadow-sm p-6 rounded-lg">
                <h2 className="text-2xl font-semibold text-custom-blue mb-4">Current Contact Numbers</h2>
                {loading ? (
                    <div className="text-center text-gray-600">Loading...</div>
                ) : Object.keys(contactNumbers).length === 0 ? (
                    <div className="text-center text-gray-600">No contact numbers available.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-custom-blue uppercase tracking-wider">Package Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-custom-blue uppercase tracking-wider">Phone</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-custom-blue uppercase tracking-wider">WhatsApp</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-custom-blue uppercase tracking-wider">Secondary Phone</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-custom-blue uppercase tracking-wider">Active</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {packageTypes.map((type) => (
                                    <tr key={type}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-custom-blue">{type}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-custom-blue">
                                            {contactNumbers[type]?.phone || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-custom-blue">
                                            {contactNumbers[type]?.whatsapp || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-custom-blue">
                                            {contactNumbers[type]?.phone2 || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-custom-blue">
                                            {contactNumbers[type]?.isActive ? 'Yes' : 'No'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminContactManagement;